/**
 * `next`: the console page an admin goes back to after signing in again (console error pages, the
 * 401 page, owner 2026-09-27).
 *
 * It travels in the address, /admin/signed-out?next=… → /admin/login?next=… → the 2FA page → the
 * page, so anyone can write a link that carries one. Every reader runs it through safeAdminNext:
 * a console path on this origin, or nothing. An open redirect here would turn the console's own
 * sign-in page into a trusted hop for a phishing link.
 */

const MAX_NEXT = 2048

/**
 * The sign-in pages (app/(admin-auth)/admin/*). Returning to one after signing in would loop, so
 * none is a `next`; admin-next.spec.ts reads the folder so a new one is covered the day it appears.
 */
const SIGN_IN_PAGES = new Set(['login', 'set-password', 'setup-2fa', 'verify-2fa', 'impersonate', 'signed-out'])

/** Any origin works for resolving: only whether the path stays on it matters. */
const PROBE_ORIGIN = 'https://console.invalid'

/**
 * `raw` when it is a console path an admin may be sent back to, normalised; otherwise null.
 *
 * Exactly `/admin`, or a path under `/admin/`, optionally with a query and a fragment. Refused
 * outright, before any parsing: `//` anywhere (another host, or a path a browser collapses), a
 * backslash (browsers read it as `/`), a dot segment, an encoded dot, slash, backslash or NUL (the
 * URL parser reads `%2e%2e` as `..`), whitespace or a control character. Then the value must
 * resolve to the same origin with a console pathname that is not a sign-in page.
 */
export function safeAdminNext(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_NEXT) return null
  if (!/^\/admin(?:[/?#]|$)/.test(raw)) return null
  if (/\/\/|\\|%2e|%2f|%5c|%00/i.test(raw)) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(raw)) return null
  if (/(^|\/)\.{1,2}(?=$|[/?#])/.test(raw)) return null
  let url: URL
  try {
    url = new URL(raw, PROBE_ORIGIN)
  } catch {
    return null
  }
  if (url.origin !== PROBE_ORIGIN) return null
  if (url.pathname !== '/admin' && !url.pathname.startsWith('/admin/')) return null
  const first = url.pathname.split('/')[2]
  if (first && SIGN_IN_PAGES.has(first)) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/** `path` with `?next=` when `next` is safe; `path` alone when it is not. */
export function withAdminNext(path: string, next: string | null | undefined): string {
  const safe = safeAdminNext(next)
  return safe ? `${path}?next=${encodeURIComponent(safe)}` : path
}

/** The "You've been signed out" page, carrying the page the admin was on. */
export const adminSignedOutHref = (next: string | null | undefined): string => withAdminNext('/admin/signed-out', next)

/** The sign-in page, carrying where to go after it. */
export const adminLoginHref = (next: string | null | undefined): string => withAdminNext('/admin/login', next)

/** `next` from a page's own query string (this page's when none is given), when it is safe. */
export function readAdminNext(search?: string): string | null {
  const source = search ?? (typeof window === 'undefined' ? '' : window.location.search)
  return safeAdminNext(new URLSearchParams(source).get('next'))
}

/** Where to land after signing in: `next` when it is safe, else the console's front door. */
export const afterSignInPath = (search?: string): string => readAdminNext(search) ?? '/admin'

/** The console page this browser is on now, when it is one an admin could come back to. */
export function currentConsolePath(): string | null {
  if (typeof window === 'undefined') return null
  const { pathname, search, hash } = window.location
  return safeAdminNext(`${pathname}${search}${hash}`)
}
